export async function delay(ms: number): Promise<void> {
    return new Promise(resolve => setTimeout(() => resolve(), ms))
}

export async function timeout<T>(work: Promise<T>, ms: number, message?: string): Promise<T> {
    const error_with_good_stack = new Error(message ?? `Timeout after ${ms}ms`)
    let timer: ReturnType<typeof setTimeout> | undefined
    const throwAfterTimeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(error_with_good_stack), ms)
    })
    try {
        return await Promise.race([work, throwAfterTimeout])
    } finally {
        clearTimeout(timer)
    }
}

async function runWithConcurrency<T, O>(
    items: T[],
    work: (item: T, index: number) => Promise<O>,
    concurrency: number
): Promise<O[]> {
    const results: O[] = new Array(items.length)
    let nextIndex = 0
    let firstError: unknown = undefined

    async function worker(): Promise<void> {
        while (nextIndex < items.length) {
            if (firstError !== undefined) return
            const i = nextIndex++
            try {
                results[i] = await work(items[i], i)
            } catch (err) {
                firstError = err
                return
            }
        }
    }

    await Promise.all(Array.from({ length: Math.min(concurrency, items.length) }, worker))

    if (firstError !== undefined) {
        throw firstError
    }

    return results
}

export async function pmap<T, O>(
    items: readonly T[],
    work: (item: T, index: number) => Promise<O>,
    options?: {
        concurrency: number
    }
): Promise<O[]> {
    if (items.length === 0) {
        return []
    }

    if (options?.concurrency) {
        return runWithConcurrency([...items], work, options.concurrency)
    }

    return Promise.all(items.map(work))
}
